import { useState } from 'react';
import { campaigns } from '@/mocks/campaigns';

const purposes = [
  { id: 'mesaj', label: 'Daha Fazla Mesaj', icon: 'ri-message-3-line', desc: 'WhatsApp & DM artır' },
  { id: 'randevu', label: 'Randevu Artır', icon: 'ri-calendar-check-line', desc: 'Doluluk oranını yükselt' },
  { id: 'satis', label: 'Satış Artır', icon: 'ri-shopping-bag-3-line', desc: 'Paket satışlarını hızlandır' },
];

const platforms = [
  { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', color: '#e1306c' },
  { id: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line', color: '#1877f2' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', color: '#25d366' },
];

const budgets = [
  { id: '3000', label: '3.000 TL', sub: 'Başlangıç' },
  { id: '5000', label: '5.000 TL', sub: 'Önerilen' },
  { id: '10000', label: '10.000 TL', sub: 'Maksimum Etki' },
];

export default function AdOrderSection() {
  const [step, setStep] = useState(1);
  const [purpose, setPurpose] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [budget, setBudget] = useState('');
  const [campaign, setCampaign] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const togglePlatform = (id: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const canNext = () => {
    if (step === 1) return !!purpose;
    if (step === 2) return selectedPlatforms.length > 0;
    if (step === 3) return !!budget;
    if (step === 4) return !!campaign;
    return false;
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const stepLabels = ['Amaç', 'Platform', 'Bütçe', 'Kampanya'];

  return (
    <section id="reklam-siparis" className="relative py-20 md:py-28 bg-[#06060b] overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#00d9ff]/4 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#d4af37]/4 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00d9ff]/30 bg-[#00d9ff]/5 text-[#00d9ff] text-xs font-semibold uppercase tracking-widest mb-5">
            <i className="ri-megaphone-line"></i>
            Reklam Modülü
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
            İstediğiniz Zaman{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d9ff] to-[#d4af37]">
              Reklam Başlatın
            </span>
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
            Karmaşık reklam panelleri yok. Sadece 4 adımda reklam talebinizi oluşturun, ekibimiz yönetsin.
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-0 mb-10">
          {stepLabels.map((label, i) => {
            const num = i + 1;
            const isActive = step === num;
            const isDone = step > num;
            return (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      isDone
                        ? 'bg-[#d4af37] text-black'
                        : isActive
                        ? 'bg-gradient-to-br from-[#00d9ff] to-[#0099bb] text-black'
                        : 'bg-white/5 text-white/30 border border-white/10'
                    }`}
                  >
                    {isDone ? <i className="ri-check-line text-sm"></i> : num}
                  </div>
                  <span className={`text-xs whitespace-nowrap ${isActive ? 'text-white' : 'text-white/30'}`}>
                    {label}
                  </span>
                </div>
                {i < stepLabels.length - 1 && (
                  <div className={`w-16 md:w-24 h-px mx-2 mb-5 transition-all duration-500 ${step > num ? 'bg-[#d4af37]/60' : 'bg-white/10'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 md:p-10 min-h-[320px]">
          {!submitted ? (
            <>
              {/* Step 1 */}
              {step === 1 && (
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">Reklam amacınız nedir?</h3>
                  <p className="text-white/40 text-sm mb-7">Hedefinize göre en uygun strateji oluşturulur.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {purposes.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setPurpose(p.id)}
                        className={`rounded-xl border p-5 text-left transition-all duration-200 cursor-pointer group ${
                          purpose === p.id
                            ? 'border-[#d4af37] bg-[#d4af37]/10'
                            : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                        }`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${purpose === p.id ? 'bg-[#d4af37]/20' : 'bg-white/5'}`}>
                          <i className={`${p.icon} text-xl ${purpose === p.id ? 'text-[#d4af37]' : 'text-white/40'}`}></i>
                        </div>
                        <div className={`font-bold text-sm mb-1 ${purpose === p.id ? 'text-[#d4af37]' : 'text-white'}`}>{p.label}</div>
                        <div className="text-white/40 text-xs">{p.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">Platform seçin</h3>
                  <p className="text-white/40 text-sm mb-7">Birden fazla platform seçebilirsiniz.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {platforms.map((p) => {
                      const isSelected = selectedPlatforms.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => togglePlatform(p.id)}
                          className={`rounded-xl border p-6 text-center transition-all duration-200 cursor-pointer ${
                            isSelected ? 'border-[#00d9ff] bg-[#00d9ff]/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                          }`}
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-3" style={{ backgroundColor: `${p.color}20` }}>
                            <i className={`${p.icon} text-2xl`} style={{ color: p.color }}></i>
                          </div>
                          <div className={`font-bold text-sm ${isSelected ? 'text-[#00d9ff]' : 'text-white'}`}>{p.label}</div>
                          {isSelected && (
                            <div className="mt-2 flex items-center justify-center gap-1 text-[#00d9ff] text-xs">
                              <i className="ri-check-line"></i> Seçildi
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">Aylık bütçeniz?</h3>
                  <p className="text-white/40 text-sm mb-7">Reklam yönetimi ek ücrete tabidir.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {budgets.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => setBudget(b.id)}
                        className={`rounded-xl border p-6 text-center transition-all duration-200 cursor-pointer ${
                          budget === b.id ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                        }`}
                      >
                        <div className={`text-2xl font-black mb-1 ${budget === b.id ? 'text-[#d4af37]' : 'text-white'}`}>{b.label}</div>
                        <div className={`text-xs ${budget === b.id ? 'text-[#d4af37]/70' : 'text-white/40'}`}>{b.sub}</div>
                        {b.id === '5000' && (
                          <div className="mt-2 inline-block px-2 py-0.5 rounded-full bg-[#d4af37]/20 text-[#d4af37] text-xs font-semibold">
                            Önerilen
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-white/25 text-xs mt-5 flex items-center gap-1.5">
                    <i className="ri-information-line text-[#d4af37]/50"></i>
                    Reklam yönetimi ek ücrete tabidir. Detaylar için ekibimiz sizinle iletişime geçer.
                  </p>
                </div>
              )}

              {/* Step 4 */}
              {step === 4 && (
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">Hangi kampanya için?</h3>
                  <p className="text-white/40 text-sm mb-7">Reklamı bağlamak istediğiniz kampanyayı seçin.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {campaigns.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setCampaign(c.id)}
                        className={`rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer ${
                          campaign === c.id ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                        }`}
                      >
                        <div className={`font-bold text-sm mb-1 ${campaign === c.id ? 'text-[#d4af37]' : 'text-white'}`}>{c.title}</div>
                        <div className="text-white/40 text-xs">{c.shortDesc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
                <button
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-white/50 text-sm font-medium transition-all hover:border-white/20 hover:text-white cursor-pointer whitespace-nowrap ${step === 1 ? 'opacity-0 pointer-events-none' : ''}`}
                >
                  <i className="ri-arrow-left-line"></i>
                  Geri
                </button>

                {step < 4 ? (
                  <button
                    onClick={() => canNext() && setStep((s) => s + 1)}
                    className={`flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      canNext()
                        ? 'bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-black hover:opacity-90'
                        : 'bg-white/5 text-white/20 cursor-not-allowed'
                    }`}
                  >
                    Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </button>
                ) : (
                  <button
                    onClick={() => canNext() && handleSubmit()}
                    className={`flex items-center gap-2 px-7 py-2.5 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      canNext()
                        ? 'bg-gradient-to-r from-[#00d9ff] to-[#0099bb] text-black hover:opacity-90'
                        : 'bg-white/5 text-white/20 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-megaphone-line"></i>
                    Reklam Talebi Oluştur
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#d4af37]/20 mb-5">
                <i className="ri-check-double-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-white font-black text-2xl mb-2">Talebiniz Alındı!</h3>
              <p className="text-white/50 text-sm max-w-sm">
                Reklam talebiniz ekibimize iletildi. En kısa sürede sizinle iletişime geçeceğiz.
              </p>
              <button
                onClick={() => { setSubmitted(false); setStep(1); setPurpose(''); setSelectedPlatforms([]); setBudget(''); setCampaign(''); }}
                className="mt-6 px-6 py-2.5 rounded-full border border-white/10 text-white/50 text-sm hover:border-white/20 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Yeni Talep Oluştur
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
